// Icons — DIRECT frontend rendering of propeller PAs (artsrc/ symlink ->
// vibes/art). An icon key resolves to a PA (artsrc/pas/, gen_pas.mjs): either
// its own pas/<key>.pa.json, or — for palette-only variants (materials/tiers/
// woods) — a pas/index.json row {pa, sets} naming the shared canonical PA and
// the per-layer color sets to render it with (colors are a renderer input;
// one PA serves every colorway). Layers reference source props which we
// fetch, then compose with the propeller-art module (symlinked from
// vibes/propeller/ts). No baked art.
// Unknown keys resolve via vsj.infos[id].icon before falling back to a
// deterministic 2-letter placeholder. Animal/creature icons pass a colors
// override: "creature_<first>_<second>" = the base (second) animal's PA
// rendered in the modifier (first) animal's colorway
// (artsrc/animal_colors.json) — no sentinel substitution, colors are a
// first-class renderer input. Tier glyphs + NPC portraits are hand-SVG
// masters, rendered natively.

import { el } from './kit';
import * as vsj from './vsj';
import {
  normalizePA, normalizeProp, paToSVG,
  type ColorSchemes, type PA, type Palette, type Prop,
} from './propeller-art';

const ART = 'artsrc';        // symlink -> vibes/art (props, PAs, schemes, colorways)
const HAND = 'assets';       // web/assets (hand-SVG masters: npcs/, tiers/)

// ---------- PA loading / composing ----------
let schemesP: Promise<ColorSchemes | null> | null = null;
const paCache = new Map<string, Promise<PA | null>>();
const propCache = new Map<string, Promise<Prop | string | null>>();
const svgTextCache = new Map<string, Promise<string | null>>();
const urlCache = new Map<string, Promise<string | null>>();

async function fetchJSON(path: string): Promise<unknown | null> {
  try {
    const r = await fetch(path);
    return r.ok ? await r.json() : null;
  } catch {
    return null;
  }
}

// pas/index.json: icon key -> {pa, sets} for palette-only variants that share
// a canonical PA (gen_pas.mjs). Keys without a row have their own PA file.
type VariantRow = { pa: string; sets: (string | null)[] };
let variantsP: Promise<Record<string, VariantRow>> | null = null;
function loadVariants(): Promise<Record<string, VariantRow>> {
  variantsP ??= (fetchJSON(`${ART}/pas/index.json`) as Promise<{ variants?: Record<string, VariantRow> } | null>)
    .then((j) => j?.variants ?? {});
  return variantsP;
}

function loadPA(key: string): Promise<PA | null> {
  let p = paCache.get(key);
  if (!p) {
    p = (async () => {
      const row = (await loadVariants())[key];
      if (!row) {
        const j = await fetchJSON(`${ART}/pas/${key}.pa.json`);
        return j ? normalizePA(j) : null;
      }
      // palette-only variant: the canonical PA with this key's color sets
      const base = await loadPA(row.pa);
      if (!base) return null;
      return { ...base, layers: base.layers.map((l, i) => ({ ...l, set: row.sets[i] ?? null })) };
    })();
    paCache.set(key, p);
  }
  return p;
}

/** Resolve a PA-layer ref (relative to artsrc/pas/) to a server path. */
function resolveRef(ref: string): string {
  const out: string[] = ['pas'];
  for (const seg of ref.split('/')) {
    if (!seg || seg === '.') continue;
    if (seg === '..') out.pop();
    else out.push(seg);
  }
  return `${ART}/${out.join('/')}`;
}

function loadLayerRes(ref: string, isRawSVG: boolean): Promise<Prop | string | null> {
  const path = resolveRef(ref);
  let p = propCache.get(path);
  if (!p) {
    p = isRawSVG
      ? fetch(path).then((r) => (r.ok ? r.text() : null)).catch(() => null)
      : fetchJSON(path).then((j) => (j ? normalizeProp(j) : null));
    propCache.set(path, p);
  }
  return p;
}

const colorsKey = (colors: Palette | null): string =>
  colors ? Object.keys(colors).sort().map((k) => `${k}:${colors[k]}`).join(',') : '';

/** Rendered SVG text for an icon key (null if it has no PA or a ref 404s). */
export function artSVG(key: string, colors: Palette | null = null): Promise<string | null> {
  const ck = `${key}|${colorsKey(colors)}`;
  let p = svgTextCache.get(ck);
  if (!p) {
    p = (async () => {
      const pa = await loadPA(key);
      if (!pa) return null;
      const props: Record<string, Prop | string> = {};
      for (const l of pa.layers) {
        if (l.hidden) continue;
        const ref = l.prop ?? l.svg;
        if (!ref) return null;
        const res = await loadLayerRes(ref, !!l.svg);
        if (res == null) return null;
        props[ref] = res;
      }
      const schemes = await (schemesP ??= fetchJSON(`${ART}/schemes.json`) as Promise<ColorSchemes | null>);
      return paToSVG(pa, props, schemes, { colors, memo: true });
    })().catch(() => null);
    svgTextCache.set(ck, p);
  }
  return p;
}

/** Object URL of the rendered icon (for canvas Image / <img> use). */
export function artImageURL(key: string): Promise<string | null> {
  let p = urlCache.get(key);
  if (!p) {
    p = artSVG(key).then((svg) =>
      svg ? URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' })) : null);
    urlCache.set(key, p);
  }
  return p;
}

// ---------- placeholders ----------
const PLACEHOLDER_RAMP = [
  '#7a5a3a', '#5d6b4a', '#4d7ea8', '#8a6fb8', '#b8703a',
  '#6b8a8a', '#9a8a4a', '#7a4a4a', '#4a7a5f', '#6a5a7a',
];

export function placeholderColor(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 1000000007;
  return PLACEHOLDER_RAMP[h % PLACEHOLDER_RAMP.length];
}

function placeholderNode(key: string, size: number): HTMLElement {
  const label = key.replace(/^(item_|tpl_|creature_)/, '').slice(0, 2).toUpperCase();
  return el(
    'div',
    {
      class: 'ph',
      style: `background:${placeholderColor(key)};font-size:${Math.max(9, size * 0.38)}px`,
    },
    label,
  );
}

// ---------- icon elements ----------
async function fillArt(holder: HTMLElement, key: string, size: number, colors: Palette | null = null): Promise<void> {
  let svg = await artSVG(key, colors);
  if (svg == null) {
    const redirect = vsj.info(key)?.icon;
    if (redirect && String(redirect) !== key) svg = await artSVG(String(redirect), colors);
  }
  if (svg == null) holder.append(placeholderNode(key, size));
  else holder.innerHTML = svg;
}

/** Icon element for any icon key (fixed square, `size` px). */
export function texture(key: string | null | undefined, size = 32): HTMLElement {
  const holder = el('div', {
    class: 'k-icon',
    style: `width:${size}px;height:${size}px`,
  });
  if (!key) {
    holder.append(placeholderNode('??', size));
    return holder;
  }
  const beast = beastParts(key);
  if (beast) {
    void fillBeast(holder, beast[0], beast[1], size);
    return holder;
  }
  if (key.startsWith('npc_')) {
    const img = el('img', { src: `${HAND}/npcs/${key}.svg`, alt: '' });
    img.onerror = () => {
      img.remove();
      holder.append(placeholderNode(key, size));
    };
    holder.append(img);
    return holder;
  }
  void fillArt(holder, key, size);
  return holder;
}

// ---------- runtime-recolored animal art (colors as a renderer input) ----------
let animalColors: Promise<Record<string, Palette>> | null = null;

/** [base id, colorway id] for animal_/creature_ keys, else null. */
function beastParts(key: string): [string, string] | null {
  if (key.startsWith('animal_')) {
    const id = key.slice('animal_'.length);
    return id ? [id, id] : null;
  }
  if (!key.startsWith('creature_')) return null;
  const parts = key.slice('creature_'.length).split('_');
  if (parts.length < 2 || !parts[0]) return null;
  return [parts.slice(1).join('_'), parts[0]];
}

async function fillBeast(holder: HTMLElement, base: string, colorway: string, size: number): Promise<void> {
  animalColors ??= (fetchJSON(`${ART}/animal_colors.json`) as Promise<Record<string, Palette> | null>).then((j) => j ?? {});
  const table = await animalColors;
  // unknown modifier colorway: the PA's own default set (the base's colors)
  const colors = table[colorway] ?? null;
  await fillArt(holder, `animal_${base}`, size, colorway === base ? null : colors);
}

/** Icon in a sunken well — backs every floating icon >= 22px. */
export function plated(key: string | null | undefined, size = 32, pad = 6): HTMLElement {
  return el(
    'div',
    { class: 'k-well', style: `width:${size + pad * 2}px;height:${size + pad * 2}px` },
    texture(key, size),
  );
}

/** Silhouette treatment (locked cards / unknown foes). */
export function silhouette(key: string | null | undefined, size = 32): HTMLElement {
  const t = texture(key, size);
  t.classList.add('silhouette');
  return t;
}

// ---------- creature tier badge ----------
/** Tier glyph tinted via CSS mask + tier name — THE creature power marker. */
export function tierBadge(tier: string, withLabel = true): HTMLElement {
  const badge = el('span', {
    style: 'display:inline-flex;align-items:center;gap:4px;font-size:var(--font-small)',
  });
  badge.append(tierGlyph(tier, 14));
  if (withLabel)
    badge.append(el('span', { style: `color:var(--tier-${tier})`, text: tier }));
  return badge;
}

export function tierGlyph(tier: string, size = 14): HTMLElement {
  const url = `${HAND}/tiers/${tier}_icon.svg`;
  return el('span', {
    style:
      `display:inline-block;width:${size}px;height:${size}px;flex:none;` +
      `background-color:var(--tier-${tier}, var(--text-dim));` +
      `-webkit-mask:url("${url}") center/contain no-repeat;` +
      `mask:url("${url}") center/contain no-repeat;`,
  });
}

/** The Map tab's drawn 3x3 biome-square glyph. */
export function mapTabGlyph(size = 20): HTMLElement {
  const grid = ['forest', 'meadows', 'mountain', 'meadows', 'forest', 'swamp', 'jungle', 'meadows', 'forest'];
  const g = el('span', {
    style:
      `display:grid;grid-template-columns:repeat(3,1fr);gap:1px;` +
      `width:${size}px;height:${size}px;flex:none`,
  });
  for (const b of grid) g.append(el('span', { style: `background:var(--biome-${b})` }));
  return g;
}
